import React, { FC, KeyboardEvent, useEffect, useState } from 'react';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import {
  Button, Checkbox, Input, Table, TablePaginationConfig,
} from 'antd';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { AlignType } from 'rc-table/lib/interface';
import { SearchOutlined } from '@ant-design/icons';

import {
  createProfileServiceUser,
  deleteProfileServiceUser,
  getMetaDataProvider,
  getProfileServiceUsers,
  updateProfileServiceUser,
  UserParameters,
} from '../service/UserService';
import UserDialog from './UserDialog/UserDialog';
import { getLogin, getProfile } from '../service/permissionService';
import YesNoDialog from '../component/YesNoDialog';
import { displayError } from '../component/Message/Message';

import User from '../model/User';
import UserPermissions from '../model/userPermissions';

import './UserTable.less';

/**
 * Display a paginated list of profile service users.
 */
const UserTable: FC<{ permissions: UserPermissions, accessToken: string }> = ({ permissions, accessToken }) => {
  const nullUser: User = {
    username: '',
    email: '',
    forename: '',
    surname: '',
    metadata: null,
    roleNames: [],
    enabled: true,
    active: true,
  };

  const [isLoading, setLoading] = useState<boolean>(true);
  const [users, setUsers] = useState<User[]>([]);
  const [params, setParams] = useState<UserParameters>({ pageSize: 9, page: 1, sort: 'ascending', enabled: true });
  const [total, setTotal] = useState<number>();
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User>(nullUser);
  const [profile, setProfile] = useState<any>({
    providerId: '',
    groups: [
      {
        name: '',
        providers: [],
      },
    ],
  });
  const [login, setLogin] = useState('');

  const { t } = useTranslation();

  /**
   * Generate column filters for an UserTable. To be used in the column definitions.
   *
   * @param title Heading of the column.
   * @param dataIndex Index of the column.
   */
  const filterColumn = (title: string, dataIndex: string) => {
    const handleReset = (clearFilters: any) => {
      clearFilters();
    };

    const handleSearch = (
      close: boolean,
      selectedKeys: any,
      confirm: any,
      dataIndex: string,
      event?: KeyboardEvent<HTMLInputElement>,
    ) => {
      confirm({ closeDropdown: close });

      var userParameters: UserParameters = {
        pageSize: 10,
        page: 1,
        filterField: dataIndex,
        filterValue: selectedKeys[0],
        sort: 'ascending',
        enabled: params.enabled,
      };
      setParams(userParameters);

      if (event) event.stopPropagation();
    };

    return ({
      filterDropdown: ({
                         setSelectedKeys,
                         selectedKeys,
                         confirm,
                         clearFilters,
                       }: any) => (
        <div className="column-filter">
          <Input
            autoFocus
            placeholder={`Search ${title}`}
            value={selectedKeys[0]}
            onChange={(event) => setSelectedKeys(event.target.value ? [event.target.value] : [])}
            onPressEnter={(event) => handleSearch(true, selectedKeys, confirm, dataIndex, event)}
          />
          <br />
          <Button
            type="primary"
            onClick={() => handleSearch(true, selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
          >
            Reset
          </Button>
          <Button
            type="link"
            onClick={() => handleSearch(false, selectedKeys, confirm, dataIndex)}
            size="small"
          >
            Filter
          </Button>
        </div>
      ),
      filterIcon: (<SearchOutlined />),
      filterMultiple: false,
    });
  };

  const fetchUsers = (token: string, userparams: UserParameters) => {
    if (token) {
      setLoading(true);
      getProfileServiceUsers(token, userparams, t)
        .then((data) => {
          setUsers(data.data.data);
          setTotal((data.data.count));
        })
        .finally(() => setLoading(false));
    } else {
      displayError('Unable to retrieve the access token');
    }
  };

  useEffect(() => {
    fetchUsers(accessToken, params);
    setProfile(getProfile(accessToken));
    setLogin(getLogin(accessToken));
  }, [params]);

  const handleChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<User> | SorterResult<User>[],
  ) => {
    const changeParams: UserParameters = {
      pageSize: pagination.pageSize || 10,
      page: pagination.current || 1,
      sort: 'ascending',
      enabled: params.enabled,
    };

    if (!Array.isArray(sorter)) {
      changeParams.sortField = sorter.field as string;
      changeParams.sort = sorter.order as string;
    }

    if (filters) {
      Object.keys(filters).forEach((column) => {
        const value = filters[column];
        if (value) {
          changeParams.filterField = column;
          changeParams.filterValue = value[0].toString();
        }
      });
    }

    setParams(changeParams);
  };

  const openUserNewDialog = () => {
    setSelectedUser(nullUser);
    setCreateUserDialogOpen(true);
  };

  const createNewUser = (user: User | boolean): Promise<boolean> => {
    if (typeof user !== 'boolean') {
      return createProfileServiceUser(accessToken, user, t)
        .then((response) => {
          if (response) setCreateUserDialogOpen(false);
          return Promise.resolve(response);
        });
    } else {
      setCreateUserDialogOpen(false);
      return Promise.resolve(false);
    }
  };

  const openEditUserDialog = (user: User) => {
    setSelectedUser(user);
    setEditUserDialogOpen(true);
  };

  const updateExistingUser = (user: User | boolean): Promise<boolean> => {
    if (typeof user !== 'boolean') {
      return updateProfileServiceUser(accessToken, user, t)
        .then((response) => {
          if (response) {
            fetchUsers(accessToken, params);
            setEditUserDialogOpen(false);
          }
          return Promise.resolve(response);
        });
    } else {
      setEditUserDialogOpen(false);
      return Promise.resolve(false);
    }
  };

  const openDeleteUserDialog = (user: User) => {
    setSelectedUser(user);
    setDeleteUserDialogOpen(true);
  };

  const deleteUser = (confirmed: boolean): Promise<boolean> => {
    if (confirmed) {
      return deleteProfileServiceUser(accessToken, selectedUser.username, t)
        .then((response) => {
          if (response) {
            fetchUsers(accessToken, params);
            setDeleteUserDialogOpen(false);
          }
          return Promise.resolve(response);
        });
    } else {
      setDeleteUserDialogOpen(false);
      return Promise.resolve(false);
    }
  };

  const renderBoolean = (value: boolean): string => (value ? t('user.table.value.true') : t('user.table.value.false'));

  const renderEditButton = (user: User): React.JSX.Element | string => {
    if (permissions.canUpdateUser) {
      return (
        <Button key="edit" title={`${t('user.table.edit.title')}`} onClick={() => openEditUserDialog(user)}>
          {t('user.table.edit')}
        </Button>
      );
    }
    return '';
  };

  const renderDeleteButton = (user: User): React.JSX.Element | string => {
    if (permissions.canDeleteUser && user.username !== login) {
      return (
        <Button key="delete" title={`${t('user.table.delete.title')}`} onClick={() => openDeleteUserDialog(user)}>
          {t('user.table.delete')}
        </Button>
      );
    }
    return '';
  };

  const columns = [
    {
      title: t('user.table.name'),
      dataIndex: 'name',
      render: (_: any, user: User) => `${user?.forename} ${user?.surname}`,
      hidden: false,
      sorter: true,
      ...filterColumn(t('user.table.name'), 'name'),
    },
    {
      title: t('user.table.email'),
      dataIndex: 'email',
      hidden: false,
      sorter: true,
      ...filterColumn(t('user.table.email'), 'email'),
    },
    {
      title: t('user.table.provider'),
      dataIndex: 'provider',
      render: (_: any, user: User) => getMetaDataProvider(user.metadata),
      hidden: !permissions.canSystemAdmin,
      sorter: true,
      ...filterColumn(t('user.table.provider'), 'provider'),
    },
    {
      title: t('user.table.roles'),
      dataIndex: 'role',
      render: (_: any, user: User) => user?.roleNames.join(', '),
      hidden: false,
      sorter: true,
      ...filterColumn(t('user.table.roles'), 'role'),
    },
    {
      title: t('user.table.enabled'),
      render: (user: User) => renderBoolean(user.enabled),
      hidden: false,
      align: 'center' as AlignType,
    },
    {
      title: t('user.table.active'),
      render: (user: User) => renderBoolean(user.active),
      hidden: false,
      align: 'center' as AlignType,
    },
    {
      title: t('user.table.edit'),
      render: (user: User) => (renderEditButton(user)),
      hidden: false,
      align: 'center' as AlignType,
    },
    {
      title: t('user.table.delete'),
      render: (user: User) => (renderDeleteButton(user)),
      hidden: false,
      align: 'center' as AlignType,
    },
  ]
    .filter((column) => !column.hidden); // Don't display the provider unless a system admin

  if (!permissions.canSystemAdmin && !permissions.canOrgAdmin) {
    return (
      <div>Access Denied</div>
    );
  }

  return (
    <div className="user-table">
      {
        permissions.canCreateUser && (
          <>
            {
              !isLoading && (
                <div className="user-buttons">
                  <label title={t('user.table.show.all.title')} className="all-user-label">
                    {t('user.table.show.all')}
                    <Checkbox
                      className="all-user-button"
                      checked={!params.enabled}
                      onChange={(e) => setParams({...params, enabled: !e.target.checked})}
                      title={t('user.table.show.all.title')}
                    />
                  </label>
                  <Button  type="primary" title={`${t('user.table.create.title')}`} onClick={openUserNewDialog}>
                    {t('user.table.create')}
                  </Button>
                </div>
              )
            }

            <UserDialog
              open={createUserDialogOpen}
              title={t('user.dialog.create.title')}
              userDetails={selectedUser}
              permissions={permissions}
              profile={profile}
              accessToken={accessToken}
              create
              onExit={createNewUser}
            />
          </>
        )
      }

      <Table
        className="enquiry-table"
        scroll={{ x: true }}
        columns={columns}
        dataSource={users}
        rowKey="username"
        loading={isLoading}
        pagination={{
          pageSize: params.pageSize,
          current: params.page,
          total,
        }}
        onChange={handleChange}
      />

      {
        permissions.canUpdateUser && (
          <UserDialog
            open={editUserDialogOpen}
            title={t('user.dialog.edit.title')}
            userDetails={selectedUser}
            permissions={permissions}
            profile={profile}
            accessToken={accessToken}
            onExit={updateExistingUser}
          />
        )
      }

      {
        permissions.canDeleteUser && (
          <YesNoDialog title={`${t('delete.user.dialog.title')} ${selectedUser.username}`} onExit={deleteUser} open={deleteUserDialogOpen}>
            {`${t('delete.user.dialog.prefix')} ${selectedUser.forename} ${selectedUser.surname}${t('delete.user.dialog.suffix')}`}
            <p />
            <b>{t('delete.user.dialog.warning')}</b>
          </YesNoDialog>
        )
      }
    </div>
  );
};

export default withAuthenticationRequired(UserTable, {});
